import { useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Package,
  Boxes,
  BarChart3,
  RefreshCw,
  Zap,
  Building2,
  ArrowUpRight,
  Sparkles,
} from "lucide-react";

const MARKETPLACES = [
  { key: "amazon", name: "Amazon", sub: "SP-API · FBM · MCF · Seller-Fulfilled Prime", color: "#232F3E", accent: "#FF9900" },
  { key: "shopify", name: "Shopify", sub: "Shop Promise · certified app", color: "#95BF47", accent: "#004C3F" },
  { key: "walmart", name: "Walmart", sub: "Marketplace API · 2-day badge", color: "#0071CE", accent: "#FFC220" },
  { key: "target", name: "Target Plus", sub: "GS1 · UPS · USPS · FedEx only", color: "#CC0000", accent: "#FFFFFF" },
  { key: "ebay", name: "eBay", sub: "Top Rated Seller compliant", color: "#E53238", accent: "#0064D2" },
];

const COMPLIANCE_MATRIX = [
  {
    area: "API / EDI integration",
    turbo: "Bi-directional real-time (SP-API, Shopify Admin, Walmart, eBay, Target+)",
    ok: true,
  },
  {
    area: "Automated tracking upload",
    turbo: "Valid tracking pushed within 24h — UPS / USPS / FedEx / DHL",
    ok: true,
  },
  {
    area: "WMS capabilities",
    turbo: "Lot tracking · expiration · SKU barcoding · serial numbers · cycle counts",
    ok: true,
  },
  {
    area: "Multi-channel inventory pooling",
    turbo: "Single inventory pool synced across all channels in real time",
    ok: true,
  },
  {
    area: "Barcoding compliance",
    turbo: "GS1 UPC · FNSKU (Amazon) · custom marketplace identifiers",
    ok: true,
  },
  {
    area: "Order cutoff / same-day dispatch",
    turbo: "2:00 PM local cutoff for same-day ship · 24h order processing",
    ok: true,
  },
  {
    area: "Return management (reverse logistics)",
    turbo: "Inspect · re-label · restock · dispose per marketplace policy",
    ok: true,
  },
  {
    area: "Unbranded packaging",
    turbo: "Plain brown / plain white · Target+ competitor-free compliant",
    ok: true,
  },
];

const SLAS = [
  { metric: ">99%", label: "On-time ship rate", sub: "Meets Amazon & Walmart threshold" },
  { metric: "<0.5%", label: "Cancellation rate", sub: "Amazon Seller Central metric" },
  { metric: "<2%", label: "Order defect rate", sub: "Walmart ODR compliant" },
  { metric: "24 hrs", label: "Tracking upload", sub: "Valid tracking to marketplace" },
  { metric: "5 days", label: "Max transit time", sub: "Target Plus requirement" },
  { metric: "US-based", label: "Fulfillment origin", sub: "Troy, MI warehouse" },
];

const CAPABILITIES = [
  {
    icon: Boxes,
    title: "WMS-grade inventory",
    body: "Lot tracking, expiration dates, SKU barcodes, serial-number recording, and cycle counts — full audit trail for every unit.",
  },
  {
    icon: RefreshCw,
    title: "Multi-channel sync",
    body: "One inventory pool across Amazon, Shopify, Walmart, Target+, and eBay. No overselling, no stockouts.",
  },
  {
    icon: Zap,
    title: "Same-day dispatch",
    body: "Orders placed before 2:00 PM EST ship the same day. Cutoff time is not a suggestion — it's a promise.",
  },
  {
    icon: BarChart3,
    title: "Live SLA dashboards",
    body: "Track on-time ship rate, cancellation rate, ODR, and tracking-upload SLAs in real time from your merchant portal.",
  },
];

const PACKAGING = [
  { title: "Plain brown", body: "Unbranded corrugated boxes — the default for Target+, Walmart, and multi-marketplace sellers.", tag: "Included" },
  { title: "Plain white", body: "Premium unbranded look for merchants who want a clean unboxing.", tag: "Included" },
  { title: "Custom-branded", body: "Your logo, colors, inserts, tissue paper, thank-you cards — full custom unboxing.", tag: "+$0.35 / order" },
  { title: "Marketplace-compliant", body: "Poly-bags for Amazon FBA prep, FNSKU labeling, box-size compliant for platform inbound.", tag: "By request" },
];

const RETURNS = [
  { step: "01", title: "Receive & scan", body: "Customer-returned parcels are received, scanned against the original order, and photographed on arrival." },
  { step: "02", title: "Inspect", body: "Trained staff grade condition (A / B / C / damaged) against marketplace return-policy criteria." },
  { step: "03", title: "Re-route", body: "A-grade goes back to stock · B-grade goes to secondary channel · damaged goes to dispose or return-to-vendor." },
  { step: "04", title: "Sync back", body: "Return status pushed to the marketplace + your merchant dashboard in real time." },
];

const Fulfillment = () => {
  const [form, setForm] = useState({
    company: "",
    email: "",
    volume: "1000-5000",
    marketplaces: "Amazon, Shopify",
    current3PL: "",
    skuCount: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="pt-14 md:pt-20">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-[var(--tt-line)] bg-white px-3 py-1.5 text-xs font-semibold text-[var(--tt-ink-soft)]">
              <span className="tt-stripe" aria-hidden><span /></span>
              <span className="uppercase tracking-[0.14em]">3PL &amp; Marketplace Fulfillment</span>
            </div>
            <h1 className="font-display mt-5 text-[44px] sm:text-6xl lg:text-[76px] leading-[0.95] font-bold text-[var(--tt-navy)] tracking-tight">
              Marketplace-ready
              <br />
              fulfillment for
              <br />
              <span className="relative inline-block">
                <span className="relative z-10 text-[var(--tt-zam-orange)]">multi-channel</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-[var(--tt-mint)]/70 -z-0 rounded-sm" />
              </span>{" "}
              sellers.
            </h1>
            <p className="mt-6 text-lg text-[var(--tt-ink-soft)] max-w-2xl leading-relaxed">
              We fulfill orders from Amazon, Shopify, Walmart, Target Plus, and
              eBay out of our Troy, MI warehouse. API-integrated, GS1-compliant,
              same-day-dispatch — and we handle returns.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="#merchant-form" className="btn-primary">
                Get a merchant quote <ArrowRight className="h-4 w-4" />
              </a>
              <a href="#compliance" className="btn-ghost">
                See compliance details
              </a>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="card p-6">
              <div className="text-xs uppercase tracking-[0.18em] text-[var(--tt-muted)] font-semibold">
                We are compliant with
              </div>
              <div className="mt-4 space-y-3">
                {MARKETPLACES.map((m) => (
                  <div key={m.key} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-[var(--tt-bg-soft)]">
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 rounded-lg grid place-items-center font-display font-bold text-[10px] uppercase tracking-wider" style={{ background: m.color, color: m.accent }}>
                        {m.name.slice(0, 3)}
                      </div>
                      <div>
                        <div className="font-semibold text-sm text-[var(--tt-navy)]">{m.name}</div>
                        <div className="text-[11px] text-[var(--tt-muted)]">{m.sub}</div>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--tt-green)]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-green)]" /> Live
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SLA GUARANTEES */}
      <section className="mt-24 md:mt-32 bg-[var(--tt-navy)] text-white py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none opacity-30" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 relative">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-zam-orange)] font-semibold flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" /> Guaranteed SLAs
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-3 leading-[1.05]">
              We hit the metrics that keep your marketplace accounts healthy.
            </h2>
            <p className="mt-4 text-white/70 max-w-lg">
              Every merchant gets a live dashboard tracking these against
              marketplace-defined thresholds. If we miss, you'll know before your
              buyer does.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-5">
            {SLAS.map((s) => (
              <div key={s.label} className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <div className="font-display text-4xl md:text-5xl font-bold text-[var(--tt-zam-orange)] leading-none">
                  {s.metric}
                </div>
                <div className="mt-3 font-semibold text-white">{s.label}</div>
                <div className="mt-1 text-xs text-white/60">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE MATRIX */}
      <section id="compliance" className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-zam-orange)] font-semibold">
              Standards checklist
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--tt-navy)] mt-3 leading-[1.05]">
              Every box, ticked.
            </h2>
            <p className="mt-4 text-[var(--tt-ink-soft)] max-w-lg">
              The full technical + operational compliance stack that
              multi-marketplace sellers require from their 3PL.
            </p>
          </div>

          <div className="card overflow-hidden">
            <div className="hidden md:grid grid-cols-[1fr_2fr_auto] gap-4 px-6 py-4 bg-[var(--tt-bg-soft)] text-[11px] uppercase tracking-wider text-[var(--tt-muted)] font-semibold border-b border-[var(--tt-line)]">
              <div>Requirement</div>
              <div>How Turbo Transit meets it</div>
              <div className="text-right">Status</div>
            </div>
            {COMPLIANCE_MATRIX.map((row, i) => (
              <div
                key={row.area}
                className={`grid md:grid-cols-[1fr_2fr_auto] gap-3 md:gap-4 px-6 py-5 items-start md:items-center ${
                  i < COMPLIANCE_MATRIX.length - 1 ? "border-b border-[var(--tt-line)]" : ""
                }`}
              >
                <div className="font-semibold text-[var(--tt-navy)] text-sm">{row.area}</div>
                <div className="text-sm text-[var(--tt-ink-soft)]">{row.turbo}</div>
                <div className="text-right">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tt-mint-soft)] text-[var(--tt-green)] px-2.5 py-1 text-[11px] font-semibold">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Compliant
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="py-20 md:py-24 bg-[var(--tt-bg-soft)] border-y border-[var(--tt-line)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-zam-orange)] font-semibold">
              Technical capabilities
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--tt-navy)] mt-3 leading-[1.05]">
              Built on modern warehouse tech.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {CAPABILITIES.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="card p-6">
                  <div className="h-11 w-11 rounded-xl bg-[var(--tt-mint-soft)] text-[var(--tt-green)] grid place-items-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-[var(--tt-navy)] mt-4">{c.title}</h3>
                  <p className="mt-2 text-sm text-[var(--tt-ink-soft)] leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PACKAGING */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-zam-orange)] font-semibold flex items-center gap-2">
                <Package className="h-3.5 w-3.5" /> Packaging integrity
              </div>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--tt-navy)] mt-3 leading-[1.05]">
                No Amazon boxes shipping to Target orders.
              </h2>
              <p className="mt-5 text-[var(--tt-ink-soft)] leading-relaxed">
                Target Plus explicitly bans competitor packaging (like Amazon FBA
                boxes). Walmart requires plain packaging with no branding. We keep
                separate inventory of unbranded corrugated so your merchants
                never violate marketplace policy.
              </p>
              <div className="mt-6 rounded-2xl bg-[var(--tt-mint-soft)] border border-[var(--tt-mint)] p-5">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 text-[var(--tt-green)] mt-0.5 shrink-0" />
                  <div className="text-sm text-[var(--tt-ink-soft)]">
                    <strong className="text-[var(--tt-navy)]">Target-Plus safe:</strong>{" "}
                    Zero competitor branding on any parcel — inspected pre-dispatch.
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {PACKAGING.map((p) => (
                <div key={p.title} className="card p-6">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-lg font-bold text-[var(--tt-navy)]">{p.title}</h3>
                    <span className="text-[10px] uppercase tracking-wider font-semibold rounded-full px-2 py-1 bg-[var(--tt-zam-orange-soft)] text-[var(--tt-zam-orange)]">
                      {p.tag}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-[var(--tt-ink-soft)] leading-relaxed">{p.body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* RETURNS */}
      <section className="py-20 md:py-28 bg-[var(--tt-navy)] text-white relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-zam-orange)] font-semibold flex items-center gap-2">
              <RefreshCw className="h-3.5 w-3.5" /> Reverse logistics
            </div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mt-3 leading-[1.05]">
              Returns — handled end-to-end.
            </h2>
            <p className="mt-4 text-white/70 max-w-lg">
              We inspect, grade, restock or dispose, and sync the result to your
              marketplace so buyers get refunded on time and your metrics stay clean.
            </p>
          </div>
          <ol className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {RETURNS.map((r, i) => (
              <li key={r.step} className="relative">
                <div className="font-display text-6xl font-bold text-[var(--tt-zam-orange)]/40 leading-none">{r.step}</div>
                {i < RETURNS.length - 1 && (
                  <div className="hidden lg:block absolute top-7 left-24 right-0 h-px bg-gradient-to-r from-white/20 to-transparent" />
                )}
                <h3 className="font-display text-xl font-bold mt-3">{r.title}</h3>
                <p className="mt-2 text-sm text-white/70 leading-relaxed pr-2">{r.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* MERCHANT ONBOARDING */}
      <section id="merchant-form" className="py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--tt-zam-orange)] font-semibold flex items-center gap-2 justify-center">
            <Sparkles className="h-3.5 w-3.5" /> Become a merchant client
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--tt-navy)] mt-3 leading-[1.05] text-center">
            Get a fulfillment quote.
          </h2>
          <p className="mt-4 text-[var(--tt-ink-soft)] max-w-xl mx-auto text-center">
            Tell us about your business. We&apos;ll review your volume and channels
            and get back within 1 business day with a per-order + storage quote.
          </p>

          {submitted ? (
            <div className="mt-10 card p-8 text-center">
              <div className="mx-auto h-14 w-14 rounded-full bg-[var(--tt-mint-soft)] grid place-items-center">
                <CheckCircle2 className="h-7 w-7 text-[var(--tt-green)]" />
              </div>
              <h3 className="font-display text-2xl font-bold text-[var(--tt-navy)] mt-5">
                Thanks — we&apos;ll be in touch within 24 hours.
              </h3>
              <p className="mt-2 text-[var(--tt-ink-soft)]">
                A fulfillment specialist will reach out to {form.email} shortly.
              </p>
            </div>
          ) : (
            <form
              onSubmit={(e) => { e.preventDefault(); setSubmitted(true); }}
              className="mt-10 card p-6 md:p-8 space-y-4"
            >
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Company name" value={form.company} onChange={(v) => setF("company", v)} placeholder="Acme Co." />
                <Field label="Work email" value={form.email} onChange={(v) => setF("email", v)} type="email" placeholder="ops@acme.com" />
              </div>
              <Select
                label="Monthly order volume"
                value={form.volume}
                onChange={(v) => setF("volume", v)}
                options={["<500", "500–1,000", "1,000–5,000", "5,000–20,000", "20,000+"]}
              />
              <Field label="Marketplaces you sell on" value={form.marketplaces} onChange={(v) => setF("marketplaces", v)} placeholder="e.g. Amazon, Shopify, Walmart" />
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Current 3PL (if any)" value={form.current3PL} onChange={(v) => setF("current3PL", v)} placeholder="Self / ShipBob / Other" />
                <Field label="Approx. SKU count" value={form.skuCount} onChange={(v) => setF("skuCount", v)} type="number" placeholder="e.g. 250" />
              </div>
              <button type="submit" className="btn-primary w-full justify-center">
                Request a quote <ArrowRight className="h-4 w-4" />
              </button>
              <p className="text-[11px] text-center text-[var(--tt-muted)]">
                In Wix, this form connects to Wix CRM with tag <code>lead:merchant-3PL</code>.
              </p>
            </form>
          )}
        </div>
      </section>

      {/* CTA BOTTOM */}
      <section className="pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="rounded-3xl bg-gradient-to-br from-[var(--tt-green)] to-[var(--tt-green-2)] p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 text-white/80 text-xs uppercase tracking-wider">
                <Building2 className="h-3.5 w-3.5" /> Not a marketplace seller?
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight mt-2">
                We also ship for individuals worldwide.
              </h3>
              <p className="text-white/80 mt-2 max-w-lg">
                Turbo Air, Turbo Bulk, suitcase shipping, and international parcels to 180+ countries.
              </p>
            </div>
            <Link to="/services" className="inline-flex items-center gap-2 rounded-full bg-white text-[var(--tt-navy)] px-6 py-3.5 font-semibold hover:bg-[var(--tt-bg)] transition">
              See personal shipping <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, value, onChange, placeholder, type = "text" }) => (
  <label className="block">
    <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--tt-muted)]">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1 w-full rounded-xl border border-[var(--tt-line)] bg-white px-3.5 py-3 text-sm outline-none focus:border-[var(--tt-green)] transition"
    />
  </label>
);

const Select = ({ label, value, onChange, options }) => (
  <label className="block">
    <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--tt-muted)]">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full rounded-xl border border-[var(--tt-line)] bg-white px-3.5 py-3 text-sm outline-none focus:border-[var(--tt-green)] transition"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </label>
);

export default Fulfillment;
